// ---------------------------------------------------------------------------
// Pure helpers for the Text Simplifier matching / safety / stale logic.
//
// These are stateless utilities so they live outside the feature factory and
// can be unit-tested in isolation. They back three fixes that landed in the
// matching/safety/stale batch:
//
//   1. Token similarity — detects "same block, changed text" when a manifest
//      block's CSS selector fails to match the live DOM (legacy b-xxx
//      selectors, Framer-specific selectors, …).
//   2. Content anchors — ancestors of manifest-applied elements form the
//      content region; anything outside all anchors is treated as
//      boilerplate rather than content.
//   3. Obvious boilerplate — nav/menu landmarks that should always be
//      skipped, independent of anchors.
// ---------------------------------------------------------------------------

/** Extract content tokens (lowercased, ≥3 chars). */
export function tokenize(s: string): Set<string> {
  const tokens = new Set<string>();
  const parts = s
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/);
  for (const t of parts) {
    if (t.length >= 3) tokens.add(t);
  }
  return tokens;
}

/** Jaccard similarity between two token sets. 1.0 = identical, 0.0 = disjoint. */
export function tokenSimilarity(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 || b.size === 0) return 0;
  let inter = 0;
  for (const t of a) if (b.has(t)) inter++;
  const uni = a.size + b.size - inter;
  return uni === 0 ? 0 : inter / uni;
}

/** Minimum token-Jaccard for two blocks to be considered "same block, stale". */
export const STALE_SIMILARITY_THRESHOLD = 0.4;

/** How many ancestor levels up to consider when anchoring content regions. */
export const CONTENT_ANCHOR_DEPTH = 4;

/** Check whether an element is a whole-page ancestor we must never anchor on.
 *  Anchoring <body> or <html> would flag every element on the page as
 *  "near content" — which defeats the whole boilerplate filter. */
function isWholePageAncestor(a: HTMLElement): boolean {
  const tag = a.tagName;
  return tag === 'BODY' || tag === 'HTML';
}

/** Build the set of ancestor containers of manifest-applied elements. */
export function buildContentAnchors(applied: HTMLElement[]): Set<HTMLElement> {
  const anchors = new Set<HTMLElement>();
  for (const el of applied) {
    let a: HTMLElement | null = el;
    for (let i = 0; i < CONTENT_ANCHOR_DEPTH && a; i++) {
      if (isWholePageAncestor(a)) break;
      anchors.add(a);
      a = a.parentElement;
    }
  }
  return anchors;
}

/** True if `el` sits within (or near) any element that has a manifest match. */
export function isNearContent(el: HTMLElement, anchors: Set<HTMLElement>): boolean {
  let a: HTMLElement | null = el;
  for (let i = 0; i < CONTENT_ANCHOR_DEPTH && a; i++) {
    if (isWholePageAncestor(a)) return false;
    if (anchors.has(a)) return true;
    a = a.parentElement;
  }
  return false;
}

/** True if `el` is inside obvious structural boilerplate (nav/menu).
 *  Footer/aside intentionally NOT included: Framer & similar frameworks
 *  misuse these tags for real content, so the content-anchor heuristic
 *  must be authoritative there. */
export function isObviousBoilerplate(el: HTMLElement): boolean {
  return !!el.closest(
    'nav, [role="navigation"], [role="menu"], [role="menubar"], [role="menuitem"]',
  );
}
